import BaseModel from './BaseModel';
import User from './User';
import Project from './Project';
import moment from 'moment';
import type { Moment } from 'moment';

/** Named canned reply template (global or project-scoped). */
class ReplyTemplate extends BaseModel {
    _id: string;
    createdAt?: Moment;
    updatedAt?: Moment;
    name: string;
    body: string;
    project?: Project | string | null;
    author?: User | string;

    constructor(properties: AnyObj = {}) {
        super({});

        this._id       = properties._id;
        this.createdAt = properties.createdAt ? moment(properties.createdAt) : undefined;
        this.updatedAt = properties.updatedAt ? moment(properties.updatedAt) : undefined;
        this.name      = properties.name || '';
        this.body      = properties.body || '';
        this.project   = properties.project?._id
            ? new Project(properties.project)
            : (properties.project ?? null);
        this.author    = properties.author?._id
            ? new User(properties.author)
            : properties.author;
    }

    isGlobal(): boolean {
        if (!this.project)
            return true;
        if (typeof this.project === 'string')
            return !this.project;
        return !(this.project as Project)._id;
    }

    projectId(): string | null {
        if (!this.project)
            return null;
        if (typeof this.project === 'string')
            return this.project || null;
        return (this.project as Project)._id || null;
    }

    /**
     * Replace `{{key}}` placeholders.
     */
    static renderBody(template: string, vars: Record<string, string> = {}): string {
        if (!template)
            return '';
        return template.replace(/\{\{\s*(\w+)\s*\}\}/g, (_match, key: string) => {
            const value = vars[key];
            return value === undefined || value === null ? '' : String(value);
        });
    }

    static buildVars(params: {
        slug?: number | string;
        title?: string;
        projectName?: string;
    } = {}): Record<string, string> {
        const slugStr = String(params.slug ?? '').trim();
        return {
            slug: slugStr,
            reference: slugStr ? `[YODA #${slugStr}]` : '[YODA #?]',
            title: String(params.title ?? '').trim(),
            project: String(params.projectName ?? '').trim(),
        };
    }

    prepareForUpdate(): AnyObj {
        return {
            _id: this._id,
            name: this.name,
            body: this.body,
            project: this.projectId(),
        };
    }
}

export default ReplyTemplate;
